// 在线报修 — kefu-service /hd-online/repair/*
// 反编译 RepairManager + RepairCreateActivity 参数
import { callInternal } from './internal.js';
import { fetchUserInfo } from './user.js';

export interface RepairCreateRequest {
	region?: RepairRegion;
	store_id: string;
	contact_user: string;
	contact_phone: string;
	store_phone?: string;
	describe: string;
	b_auto_id?: string | number;
	images?: string[];          // 图片 URL 列表（可空）
}

export type RepairRegion = 'cn' | 'apac';

export async function createRepair(req: RepairCreateRequest): Promise<{ ok: boolean; msg?: string }> {
	try {
		const region = req.region ?? 'cn';
		const images = JSON.stringify(req.images ?? []);
		const userInfo = region === 'apac' && req.b_auto_id == null ? await fetchUserInfo().catch(() => null) : null;
		const bAutoId = req.b_auto_id ?? userInfo?.b_auto_id;
		const res = await callInternal({
			short_name: 'kefu',
			action: '/hd-online/repair/create',
			method: 'POST',
			body: region === 'apac'
				? {
					store_id: req.store_id,
					from: 'android',
					...(bAutoId != null ? { b_auto_id: bAutoId } : {}),
					contact_user: req.contact_user,
					contact_phone: req.contact_phone,
					store_phone: req.store_phone ?? '',
					describe: req.describe,
					images,
				}
				: {
					store_id: req.store_id,
					from: 'web',
					contact_user: req.contact_user,
					contact_phone: req.contact_phone,
					describe: req.describe,
					images,
					device_count: 1,
				},
			av: '1',
		});
		return { ok: Number(res.code) === 0, msg: res.msg || `code=${res.code}` };
	} catch (e) {
		const msg = e instanceof Error ? e.message : String(e);
		return { ok: false, msg };
	}
}

export interface RepairItem {
	id?: string | number;
	store_id?: string;
	store_name?: string;
	contact_user?: string;
	contact_phone?: string;
	describe?: string;
	create_time?: string;
	status?: string | number;
	no?: string | number;
	[k: string]: unknown;
}

export interface RepairListResult {
	ok: boolean;
	items: RepairItem[];
	msg?: string;
}

export interface RepairDetailResult {
	ok: boolean;
	item?: RepairItem;
	msg?: string;
}

type RepairListPayload =
	| RepairItem[]
	| {
		items?: RepairItem[];
		list?: RepairItem[];
		rows?: RepairItem[];
		records?: RepairItem[];
		item?: RepairItem[] | RepairItem;
		data?: RepairListPayload;
		result?: RepairListPayload;
	};

function normalizeRepairListPayload(payload: RepairListPayload | undefined): RepairItem[] {
	if (!payload) return [];
	if (Array.isArray(payload)) return payload;
	const direct = payload.items ?? payload.list ?? payload.rows ?? payload.records ?? payload.item;
	if (Array.isArray(direct)) return direct;
	if (direct && typeof direct === 'object') return [direct];
	return normalizeRepairListPayload(payload.data ?? payload.result);
}

export async function fetchRepairListResult(opts: {
	region?: RepairRegion;
	store_id?: string;
	page?: number;
	count?: number;
	status?: string;
	b_auto_id?: string | number;
	start_date?: string;
	end_date?: string;
}): Promise<RepairListResult> {
	try {
		const region = opts.region ?? 'cn';
		const page = opts.page ?? 1;
		const pageSize = opts.count ?? 30;
		const userInfo = region === 'apac' && opts.b_auto_id == null ? await fetchUserInfo().catch(() => null) : null;
		const bAutoId = opts.b_auto_id ?? userInfo?.b_auto_id;
		const query = region === 'apac' ? {
			...(opts.store_id ? { store_id: opts.store_id } : {}),
			...(bAutoId != null ? { b_auto_id: bAutoId } : {}),
			page_num: page,
			page_size: pageSize,
			status: opts.status ?? '1,2,3',
			...(opts.start_date ? { start_date: opts.start_date } : {}),
			...(opts.end_date ? { end_date: opts.end_date } : {}),
			tz_info: 'UTC+28800',
			language: 'en',
			source: 'android',
			platform: 'android',
		} : {
			...(opts.store_id ? { store_id: opts.store_id } : {}),
			source: 'android',
			page_num: page,
			page_size: pageSize,
			status: '1,2',
			tz_info: 'UTC+28800',
			language: 'en',
		};
		const res = await callInternal<RepairListPayload>({
			short_name: 'kefu',
			action: '/hd-online/repair/list',
			method: 'GET',
			query,
			av: '1',
		});
		if (Number(res.code) !== 0) {
			return { ok: false, items: [], msg: `${res.msg || 'Request failed'} (code=${res.code})` };
		}
		return { ok: true, items: normalizeRepairListPayload(res.data) };
	} catch (e) {
		const msg = e instanceof Error ? e.message : String(e);
		return { ok: false, items: [], msg };
	}
}

export async function fetchRepairListWithDateFallback(opts: {
	region?: RepairRegion;
	store_id?: string;
	page?: number;
	count?: number;
	status?: string;
	b_auto_id?: string | number;
	start_date?: string;
	end_date?: string;
}): Promise<RepairListResult> {
	const hasDateFilter = Boolean(opts.start_date || opts.end_date);
	const dated = await fetchRepairListResult(opts);
	if (!hasDateFilter || (dated.ok && dated.items.length > 0)) return dated;
	const fallback = await fetchRepairListResult({
		...opts,
		start_date: undefined,
		end_date: undefined,
	});
	if (fallback.ok) return fallback;
	return dated.ok ? dated : fallback;
}

export async function fetchRepairList(opts: {
	region?: RepairRegion;
	store_id?: string;
	page?: number;
	count?: number;
	status?: string;
}): Promise<RepairItem[]> {
	const result = await fetchRepairListResult(opts);
	return result.items;
}

/** 删除门店 — base /store/del_store POST */
export async function fetchRepairDetailResult(id: string | number): Promise<RepairDetailResult> {
	const queries: Array<Record<string, string | number>> = [{ id }, { repair_id: id }, { no: id }];
	let lastMsg: string | undefined;
	for (const query of queries) {
		try {
			const res = await callInternal<RepairItem>({
				short_name: 'kefu',
				action: '/hd-online/repair/detail',
				method: 'GET',
				query,
				av: '1',
			});
			if (Number(res.code) === 0) {
				return { ok: true, item: res.data };
			}
			lastMsg = `${res.msg || 'Request failed'} (code=${res.code})`;
		} catch (e) {
			lastMsg = e instanceof Error ? e.message : String(e);
		}
	}
	return { ok: false, msg: lastMsg ?? '报修详情接口暂不可用' };
}

export async function remindRepair(id: string | number): Promise<{ ok: boolean; msg?: string }> {
	const attempts: Array<Record<string, string | number>> = [{ id }, { repair_id: id }];
	let lastMsg: string | undefined;
	for (const body of attempts) {
		try {
			const res = await callInternal({
				short_name: 'kefu',
				action: '/hd-online/repair/remind',
				method: 'POST',
				body,
				av: '1',
			});
			if (Number(res.code) === 0) return { ok: true, msg: res.msg };
			lastMsg = `${res.msg || 'Request failed'} (code=${res.code})`;
		} catch (e) {
			lastMsg = e instanceof Error ? e.message : String(e);
		}
	}
	return { ok: false, msg: lastMsg ?? '报修催单接口暂不可用' };
}

export async function deleteStore(storeId: string): Promise<{ ok: boolean; msg?: string }> {
	const res = await callInternal({
		short_name: 'base',
		action: '/store/del_store',
		method: 'POST',
		body: { store_id: storeId },
		av: '1',
	});
	return { ok: Number(res.code) === 0, msg: res.msg };
}
